import React, { useEffect, useState, useCallback } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import SkeletonTable from "../../Skeleton/SkeletonTable";
import cdpService from "../../../apiService/cdp.service";
import { calculateAge } from "../../utils/utils";

const CustomerTable = ({ selectedFilters }) => {
  const [customers, setCustomers] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 500);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await cdpService.getCustomerList({
        product_id: selectedFilters?.product_id || null,
        registration_device: selectedFilters?.registration_device || null,
        start_date: selectedFilters?.start_date || null,
        end_date: selectedFilters?.end_date || null,
        page: currentPage,
        per_page: perPage,
        search: debouncedSearch,
      });
      const data = response.data?.data?.customerData || {};
      setCustomers(data.customers || []);
      setTotalPages(data.totalPages || 1);
    } catch (error) {
      setCustomers([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, perPage, selectedFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="rounded-sm shadow text-gray-900 bg-white">
      {/* Header Controls */}
      <div className="w-full px-8 py-4 hidden lg:grid grid-cols-1 lg:grid-cols-2">
        <div className="flex items-center mb-4 lg:mb-0">
          <label className="text-gray-600 text-sm">Show</label>
          <select
            className="block w-auto rounded-full border-0 py-2 px-4 mx-4 text-gray-900 shadow-sm ring-1 ring-gray-300 focus:ring-gray-400 sm:text-sm"
            value={perPage}
            onChange={(e) => setPerPage(Number(e.target.value))}
          >
            {[10, 25, 50, 100].map((num) => (
              <option key={num} value={num}>{num}</option>
            ))}
          </select>
          <label className="text-gray-600 text-sm">entries</label>
        </div>
        <div className="flex items-center justify-start lg:justify-end">
          <label className="text-gray-600 text-sm">Search</label>
          <input
            type="text"
            className="block w-auto rounded-full border-0 py-2 pl-4 pr-10 lg:pr-20 ml-4 text-gray-900 shadow-sm ring-1 ring-gray-300 focus:ring-gray-400 sm:text-sm"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Table Content */}
      <div className="overflow-x-auto">
        {loading ? (
          <SkeletonTable rowCount={10} columnCount={8} />
        ) : customers.length === 0 ? (
          <div className="text-center py-6 text-gray-500">No customers found</div>
        ) : (
          <table className="table-auto w-full">
            <thead className="text-xs font-semibold uppercase text-gray-500 bg-gray-100">
              <tr>
                <th className="py-3.5 pl-8 pr-3 text-left">#</th>
                <th className="px-3 py-3.5 text-left">Name</th>
                <th className="px-3 py-3.5 text-left">Mobile No.</th>
                <th className="px-3 py-3.5 text-left">Email</th>
                <th className="px-3 py-3.5 text-left">Gender</th>
                <th className="px-3 py-3.5 text-left">DOB</th>
                <th className="px-3 py-3.5 text-left">Age</th>
                <th className="px-3 py-3.5 text-left">Product Name</th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-gray-100">
              {customers.map((customer, index) => (
                <tr key={customer.id} className="border-b border-gray-200 even:bg-gray-50">
                  <td className="py-4 pl-8 pr-3 text-sm text-gray-700">{"#JNM-" + customer.id}</td>
                  <td className="px-3 py-4 text-sm text-gray-700">
                    {customer.first_name || customer.last_name ? `${customer.first_name || ""} ${customer.last_name || ""}`.trim() : "-"}
                </td>
                  <td className="px-3 py-4 text-sm text-gray-700">{customer.mobile_no || "-"}</td>
                  <td className="px-3 py-4 text-sm text-gray-700">{customer.email || "-"}</td>
                  <td className="px-3 py-4 text-sm text-gray-700">{customer.gender || "-"}</td>
                  <td className="px-3 py-4 text-sm text-gray-700">{customer.date_of_birth || "-"}</td>
                  <td className="px-3 py-4 text-sm text-gray-700">{calculateAge(customer.date_of_birth)}</td>
                  <td className="px-3 py-4 text-sm text-gray-700">{customer.product_name || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3">
        <button
          className="px-4 py-2 text-gray-600 ring-1 ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </button>
        <span className="text-gray-700">Page {currentPage} of {totalPages}</span>
        <button
          className="px-4 py-2 text-gray-600 ring-1 ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
        >
          <ChevronRightIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default React.memo(CustomerTable);
